(ns app.front.view.inspections.checklists.checklists-v2
  (:require [app.domain.group.model :as group-model]
            [app.domain.inspection.model :as inspection-model]
            [app.front.entity-edit :as ee]
            [app.front.process :as process]
            [app.front.ui :as ui]
            [app.front.ui.accordion :as accordion]
            [app.front.ui.add-widget :as ui-add]
            [app.front.ui.radio :as ui-radio]
            [app.front.ui.rich-text :as ui-rt]
            [app.front.ui.section :as section]
            [app.front.ui.select :as ui-select]
            [app.front.util :as futil]
            [app.front.view.inspections.checklists.subs :as checklist-subs]
            [app.front.view.inspections.deficiencies-v2 :as deficiencies-v2]
            [app.front.view.workspace.subs :as ws-subs]
            [app.i18n.model :as i18n-model]
            [app.rich-text :as rich-text]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.front.date-util :as date-util]
            [core.front.event :refer [<sub >evt]]
            [core.front.icons :as i]
            [core.front.media-query :as media-query]
            [core.user.model :as user-model]
            [core.util.util :as util]
            [reagent.core :as r]))

(defn- some-but-not-every? [f coll]
  (boolean
    (and
      (not-every? f coll)
      (some f coll))))

(defn checklist-item-value->string [tr lang {:inspection.checklist/keys [custom-result-set result-set]} value]
  (if (= value :none)
    (tr [:select-option])
    (if custom-result-set
      (i18n-model/get-text-from-entities-or-map
        (->> value
             inspection-model/checklist-item-result->custom-result-set-attribute
             (get custom-result-set))
        lang)
      (tr [(keyword (str "inspection.checklist.result-set."
                         (-> result-set name))
                    (name value))]))))

(defn multi-select-controls [{:keys [tr group-id items val->str selection-atom save-multi-fn]}]
  [:<>
   (cc/button {:kind       :tertiary
               :size       :small
               :class-name "mr-2"
               :disabled   (empty? @selection-atom)
               :on-click   #(reset! selection-atom #{})}
              (tr [:multi-select/clear-selection]))
   [ui-select/select
    {:id              (str "select-multi-result-" group-id)
     :select-text     (tr [:select-option])
     :hide-label      true
     :disabled        (empty? @selection-atom)
     :allow-empty?    false
     :size            :sm
     :on-change       #(let [result (->> % (keyword "inspection.checklist-item.result-value"))]
                         (save-multi-fn
                           (set (for [[j sub-item] (map-indexed vector items)
                                      :let         [sub-result (-> sub-item inspection-model/checklist-item-result)
                                                    val (-> sub-result :inspection.checklist-item.result/value)]
                                      :when        (and (@selection-atom j) (not= val result))]
                                  (:db/id sub-result)))
                           :inspection.checklist-item.result/value
                           result))
     :val->str        val->str
     :values          (->> inspection-model/checklist-item-result-values
                           (cons :none))
     :value           (let [results (->> (map-indexed vector items)
                                         (keep (fn [[i item]]
                                                 (when (@selection-atom i)
                                                   (-> item inspection-model/checklist-item-result :inspection.checklist-item.result/value))))
                                         distinct)]
                        (if (= 1 (count results))
                          (first results)
                          :none))
     :disabled-values #{:none}}]])

(defn multi-select-item-result-radio [radio-props {:keys [item item-order items multi-enabled? selection-atom save-multi-fn]}]
  (let [result (-> item inspection-model/checklist-item-result)]
    [ui-radio/radio
     (merge radio-props
            {:values    inspection-model/checklist-item-result-values
             :value     (:inspection.checklist-item.result/value result)
             :on-change (when result
                          #(save-multi-fn
                             (if (and multi-enabled? (@selection-atom item-order))
                               (set (for [[j sub-item] (map-indexed vector items)
                                          :let         [sub-result (-> sub-item inspection-model/checklist-item-result)
                                                        val        (-> sub-result :inspection.checklist-item.result/value)]
                                          :when        (and (@selection-atom j) (not= val %))]
                                      (:db/id sub-result)))
                               #{(:db/id result)})
                             :inspection.checklist-item.result/value
                             %))})]))

(defn select-all-checkbox [group-id items selection-atom]
  (cc/checkbox
    {:id         (str "select-all-checkbox-" group-id)
     :label-text ""
     :checked    (= (count items) (count @selection-atom))
     :on-change  #(swap! selection-atom (fn [m]
                                          (if (= (count items) (count m))
                                            #{}
                                            (->> items count range set))))
     :hide-label true}))

(defn checklist-group-section [{:keys [tr lang target-entity inspection group checklist-completed? active-visit?
                                       checklist *group-errors group-counter show-results?]}]
  (let [screen-md         (<sub [::media-query/breakpoint-matches? :md])
        group-error       (nth @*group-errors group-counter) ; Deref needs to be done inside the component for correct render scope
        group-id          (:db/id group)
        items             (->> group :inspection.checklist-item/_group (sort-by :ordered/order))
        show-options?     (and active-visit? (not checklist-completed?))
        note-enabled?     (<sub [::ws-subs/has-feature? :group.feature/inspection-checklist-item-note])
        has-note?         #(-> % :inspection.checklist-item/note rich-text/empty-html-rich-text? not)
        options-width     "w-full md:w-1/2 lg:w-1/3 xxl:w-1/5"
        info-width        (if show-results?
                            "w-full md:w-1/2 lg:w-2/3 xxl:w-4/5"
                            "w-full")
        ref-code-width    "w-full md:w-2/5 lg:w-1/5 xxl:w-1/6"
        description-width "w-full md:w-3/5 lg:w-4/5 xxl:w-5/6"
        save-multi-fn     (partial (ee/multi-save-fn :process.signal/set-checklist-item-result
                                                     {:additional-data {:inspection-id (:db/id inspection)}})
                                   target-entity)]
    (r/with-let [*multi-sel?      (r/atom false)
                 *selected       (r/atom #{})
                 *notes-expanded (r/atom (->> items
                                              (filter has-note?)
                                              (map :db/id)
                                              set))]
      [:<>
       (when show-options?
         [:div.flex.justify-between.py-2.px-3
          (when @*multi-sel?
            [:div.mr-2.md:sr-only
             [select-all-checkbox group-id items *selected]])
          [:span.flex.w-full.justify-left.overflow-hidden.mr-2
           (cc/button {:kind       :tertiary
                       :size       :small
                       :class-name "mr-2"
                       :icon       (when @*multi-sel? i/remove)
                       :on-click   #(swap! *multi-sel? not)}
                      (tr [:multi-select/multi-select]))
           (when @*multi-sel?
             [multi-select-controls
              (util/mmap {:selection-atom *selected
                          :val->str       (partial checklist-item-value->string tr lang checklist)}
                         tr group-id items save-multi-fn)])]])
       [:div.hidden.md:flex.justify-between.py-0.px-3.text-xs
        {:class       "md:flex"
         :aria-hidden true}
        (when (and show-results? show-options? @*multi-sel?)
          [select-all-checkbox group-id items *selected])
        (when show-results?
          [:div.flex.justify-around.mr-4.my-2
           {:class options-width}
           (futil/for-frag [result inspection-model/checklist-item-result-values]
             [:span.text-center {:class "w-1/3"} (checklist-item-value->string tr lang checklist result)])])

        [:div.flex.justify-between.my-2
         {:class info-width}
         [:div.mr-2 {:class ref-code-width} (tr [:inspection/code-reference])]
         [:div {:class description-width} (tr [:inspection/description])]]]

       (futil/for-frag-indexed [[c item] items]
         (let [first?         (= c 0)
               result         (-> item inspection-model/checklist-item-result)
               ; Errors are shown if accordion section is opened in a state where some but not all items have answers
               error?         (and active-visit?
                                   group-error
                                   (-> result :inspection.checklist-item.result/value boolean not))
               save-item-note (partial (ee/save-fn :process.signal/set-checklist-note
                                                   {:additional-data {:inspection-id (:db/id inspection)}})
                                       target-entity
                                       (:db/id item)
                                       :inspection.checklist-item/note)
               note-expanded? (contains? @*notes-expanded (:db/id item))]
           [:div
            {:class (when (not first?) "mt-2")}
            [:div.flex.justify-between.rounded.border-solid.py-2.px-3.leading-snug
             {:class (str/join " " (concat
                                     (if error?
                                       ["border-2 border-ui-yellow"]
                                       ["border border-gray-400"])
                                     (when-not screen-md
                                       ["flex-col"])))}
             (when (and show-results? show-options? @*multi-sel?)
               (cc/checkbox {:id         (str "select-item-checkbox-" (:db/id item))
                             :label-text ""
                             :checked    (contains? @*selected c)
                             :on-change  #(swap! *selected util/toggle-in-set c)
                             :hide-label true}))
             (when show-results?
               [:div.flex.justify-between.mr-4.flex-col.md:flex-row
                {:class options-width}
                [:div.font-bold.my-2.md:sr-only
                 (tr [:inspection/checklist-result])]
                [multi-select-item-result-radio
                 {:name               (str "checklist-item-" (:db/id item))
                  :class              "w-full mt-1"
                  :group-class        (if screen-md
                                        "flex justify-around w-full"
                                        "flex-col items-baseline gap-1")
                  :hide-button-labels screen-md
                  :disabled           (not show-options?)
                  :val->str           (partial checklist-item-value->string tr lang checklist)
                  :width              "10"}
                 (util/mmap {:item-order     c
                             :multi-enabled? @*multi-sel?
                             :selection-atom *selected}
                            item items save-multi-fn)]])
             [:div.flex.justify-between.flex-col.md:flex-row
              {:class info-width}
              [:div.font-bold.my-2.md:sr-only
               (tr [:inspection/code-reference])]
              [:span.overflow-hidden.mr-2
               {:class ref-code-width}
               [ui-rt/editor-legacy {:id            (str "checklist-item-reference-" (:db/id item))
                                     :initial-value (:inspection.checklist-item/code item)}]]
              [:div.font-bold.my-2.md:sr-only
               (tr [:inspection/description])]
              [:span
               {:class description-width}
               [ui-rt/expandable-text
                (util/mmap {:id          (str "checklist-item-description-" (:db/id item) "-" lang)
                            :max-len     deficiencies-v2/description-max-length
                            :text        (-> (:inspection.checklist-item/descriptions item)
                                             (i18n-model/get-text-from-entities-or-map lang))
                            :rt-enabled? true}
                           tr)]]]]
            (when error?
              [:div.bg-ui-yellow.rounded-b.text-ui-night-sky-100.text-xs.p-1.font-bold.m-0.border-0.ml-1.w-max
               (tr [:inspection/must-choose-one-option])])
            (when show-options?
              [:<>
               [:div.flex.my-1.justify-end
                (let [label     (tr [(if note-expanded? :cancel-note :add-note)])
                      btn-props {:kind             (if note-expanded? :danger :tertiary)
                                 :size             :small
                                 :icon             (when note-expanded? i/remove)
                                 :class-name       (if note-expanded? "bg-ui-night-sky tx-ui-white" "")
                                 :icon-position    :right
                                 :icon-description label}]
                  (when note-enabled?
                    (if (and note-expanded? (has-note? item))
                      [ui/modal-confirmation-button
                       {:id                  (str "checklist-item-note-button-" (:db/id item))
                        :tr                  tr
                        :trigger-button-text label
                        :trigger-button-opts btn-props
                        :on-yes-click        #(do
                                                (save-item-note nil)
                                                (swap! *notes-expanded util/toggle-in-set (:db/id item)))}]
                      (cc/button
                        (merge btn-props {:on-click #(swap! *notes-expanded util/toggle-in-set (:db/id item))})
                        label))))]
               (when (and note-enabled? note-expanded?)
                 [:div.mt-2
                  [ui-rt/editor-legacy
                   {:id                 (str "checklist-item-note-" (:db/id item))
                    :authority-group-id (-> checklist group-model/get-authority-group :db/id)
                    :initial-value      (-> item :inspection.checklist-item/note)
                    :on-blur            save-item-note}]])])]))])))

(defn- confirm-checklist-completed-button
  [{:keys [tr target-entity inspection checklist checklist-completed?]}]
  (when (<sub [::checklist-subs/complete-permitted? checklist])
    (let [remarks?      (and (:inspection.checklist/positive-result-remark? checklist)
                             (inspection-model/checklist-has-result-value?
                               :inspection.checklist-item.result-value/pass
                               checklist))
          deficiencies? (and (:inspection.checklist/negative-result-deficiency? checklist)
                             (inspection-model/checklist-has-result-value?
                               :inspection.checklist-item.result-value/fail
                               checklist))
          btn-text      (tr [(cond
                               (and remarks? deficiencies?)
                               :inspection/checklist-complete-and-add-deficiencies-and-remarks
                               deficiencies?
                               :inspection/checklist-complete-and-add-deficiencies
                               remarks?
                               :inspection/checklist-complete-and-add-remarks
                               :else :inspection/confirm-checklist-completed)])]

      (cc/button {:kind             :primary
                  :disabled         (boolean (or (some nil? (->> checklist
                                                                 inspection-model/checklist-items
                                                                 (map inspection-model/checklist-item->result-value)))
                                                 checklist-completed?))
                  :size             :small
                  :class-name       "w-full sm:w-auto"
                  :icon             i/check
                  :icon-description btn-text
                  :on-click         #(>evt [::checklist-subs/confirm-checklist-completed (:db/id target-entity) (:db/id inspection) (:db/id checklist)])}
                 btn-text))))

(defn- remove-checklist-button [{:keys [tr lang checklist target-entity inspection]}]
  (when (<sub [::checklist-subs/remove-permitted? checklist])
    [ui/modal-confirmation-button
     {:tr                  tr
      :modal-title         (tr [:inspection/remove-checklist])
      :trigger-button-text (tr [:inspection/remove-checklist])
      :trigger-button-opts {:icon          i/trash
                            :icon-position :right
                            :size          :small
                            :kind          :tertiary
                            :class-name    "w-full sm:w-auto"}
      :on-yes-click        #(>evt [::process/signal
                                   (:db/id target-entity)
                                   [:process.signal/remove-checklist-from-inspection
                                    {:inspection-id (:db/id inspection) :checklist-id (:db/id checklist)}]
                                   {:notify {:success :inspection/checklist-removed}}])}
     [:div (tr [:inspection/remove-checklist-legend] [(-> (:inspection.checklist/names checklist)
                                                          (i18n-model/get-text-from-entities-or-map lang))])]]))

(defn- render-checklist-timestamps [tr checklist]
  (let [format-datetime (<sub [::date-util/date-time-fn])
        modified-ts     (-> checklist :inspection.checklist/last-modified-ts)
        modified-by     (-> checklist :inspection.checklist/last-modified-by)
        confirmed-ts    (-> checklist :inspection.checklist/last-confirmed-ts)
        confirmed-by    (-> checklist :inspection.checklist/last-confirmed-by)]
    [:div.text-xs.mb-8.flex.flex-col.gap-2.lg:block
     (when (and modified-ts modified-by)
       [:span
        (str/join " " [(tr [:inspection/checklist-last-modified])
                       (format-datetime modified-ts)
                       (str "(" (user-model/full-name modified-by) ")")])])
     (when (and modified-ts modified-by confirmed-ts confirmed-by)
       [:span.px-2.hidden.lg:inline-block "|"])
     (when (and confirmed-ts confirmed-by)
       [:span
        (str/join " " [(tr [:inspection/checklist-last-confirmed])
                       (format-datetime confirmed-ts)
                       (str "(" (user-model/full-name confirmed-by) ")")])])]))

(defn checklist-section [{:keys [tr lang visit checklist deficiencies?] :as props}]
  (let [screen-sm            (<sub [::media-query/breakpoint-matches? :sm])
        groups               (->> checklist inspection-model/checklist-groups (sort-by :ordered/order))
        group-error-state-fn (fn [group] (->> group
                                              inspection-model/checklist-group-items
                                              (map inspection-model/checklist-item->result-value)
                                              (some-but-not-every? nil?)))
        checklist-completed? (boolean ((set (:inspection.checklist/completed-for checklist)) visit))
        active-visit?        (boolean visit)]
    (r/with-let [*group-errors (r/atom (map group-error-state-fn groups))]
      [:div.bg-ui-blue-10.mb-8.py-4.px-3.rounded
       [:div.flex.justify-between.mb-4.flex-col.lg:flex-row
        [:h4.mb-2 (-> (:inspection.checklist/names checklist)
                      (i18n-model/get-text-from-entities-or-map lang))]
        [:div.flex.gap-4.flex-col.lg:flex-row.items-end.justify-end.flex-wrap
         [remove-checklist-button props]
         [confirm-checklist-completed-button (merge props (util/mmap checklist-completed?))]]]
       [render-checklist-timestamps tr checklist]
       [accordion/blue-header-accordion
        (str "checklist-accordion-" (:db/id checklist))
        tr
        {:initial-state :first-open
         :on-expand     #(reset! *group-errors (map group-error-state-fn groups))}
        (futil/for-indexed [[group-counter group] groups]
          (let [items                (-> group inspection-model/checklist-group-items)
                show-results?        (->> items
                                          (map inspection-model/checklist-item-result)
                                          (remove nil?)
                                          not-empty)
                answered-items-count (->> items
                                          (map inspection-model/checklist-item->result-value)
                                          (remove nil?)
                                          count)
                deficiencies-count   (when deficiencies?
                                       (->> items
                                            (map inspection-model/checklist-item->result-value)
                                            (filter #(= :inspection.checklist-item.result-value/fail %))
                                            count))]
            {:header-pill-text  (when show-results? (str answered-items-count "/" (count items)))
             :header-pill-color (when (not= answered-items-count (count items)) :yellow)
             :header-text       (-> (:inspection.checklist-item.group/names group)
                                    (i18n-model/get-text-from-entities-or-map lang))
             :header-info       (when (and deficiencies-count show-results? screen-sm)
                                  [:<>
                                   [:span (tr [:inspection/checklist-deficiencies])]
                                   [:span.pl-4.font-bold (str deficiencies-count)]])
             :content           [checklist-group-section
                                 (merge props
                                        (util/mmap group checklist-completed? active-visit? show-results?)
                                        {:group-counter group-counter
                                         :*group-errors *group-errors})]}))]
       [:div.mt-8.mb-4.text-right
        [confirm-checklist-completed-button (merge props (util/mmap checklist-completed?))]]])))

(defn add-checklist [{:keys [tr lang target-entity inspection checklists]}]
  (r/with-let [*selected-template (r/atom nil)
               *show-add-bubble   (r/atom false)]
    (when (<sub [::checklist-subs/add-permitted?])
      (let [bubble-toggle-fn      #(swap! *show-add-bubble not)
            selected-template     @*selected-template
            default-checklist-ids (<sub [::checklist-subs/default-checklist-ids])
            checklist-templates   (<sub [::checklist-subs/checklist-templates])
            used-template-ids     (->> checklists
                                       (map (comp :db/id :inspection.checklist/template))
                                       set)]
        [:div.mb-8
         (if (= @*show-add-bubble true)
           [:div {:class "lg:w-1/2 xl:w-1/3"}
            [ui-add/add-widget
             {:title    (tr [:inspection/add-checklist])
              :loading? false
              :buttons  [{:text     (tr [:cancel])
                          :on-click bubble-toggle-fn}
                         {:text      (tr [:create])
                          :disabled? (empty? selected-template)
                          :on-click  #(do (>evt [::process/signal
                                                 (:db/id target-entity)
                                                 [:process.signal/add-checklist-to-inspection
                                                  {:inspection-id         (:db/id inspection)
                                                   :checklist-template-id (:db/id selected-template)}]
                                                 {:notify {:success :inspection/checklist-added}}])
                                          (bubble-toggle-fn)
                                          (reset! *selected-template nil))}]}
             [:div
              [ui-select/select {:id              "select-checklist-template"
                                 :label-text      (tr [:inspection/select-checklist-template])
                                 :class           "w-full"
                                 :required        true
                                 :light           true
                                 :allow-empty?    false
                                 :val->str        #(-> (:inspection.checklist/names %)
                                                       (i18n-model/get-text-from-entities-or-map lang)
                                                       (str (when (default-checklist-ids (:db/id %))
                                                              (str " (" (tr [:inspection.checklist/default]) ")"))))
                                 :value           selected-template
                                 :on-change       #(reset! *selected-template %)
                                 :disabled-values (set (filter #(used-template-ids (:db/id %)) checklist-templates))
                                 :values          checklist-templates}]]]]
           (cc/button {:kind             :primary
                       :size             :small
                       :icon             i/plus
                       :disabled         (->> checklist-templates
                                              (remove #(used-template-ids (:db/id %)))
                                              seq
                                              boolean
                                              not)
                       :icon-description (tr [:inspection/add-checklist])
                       :on-click         bubble-toggle-fn}
                      (tr [:inspection/add-checklist])))]))))

(defn checklists-section [{:keys [tr inspection workspace] :as props}]
  (let [lang          (<sub [:language])
        checklists    (->> inspection
                           inspection-model/checklists
                           (sort-by :ordered/order))
        deficiencies? (-> workspace
                          :workspace/permit-type
                          inspection-model/has-deficiencies?)]
    (when (or (and (<sub [::checklist-subs/add-permitted?])
                   (seq (<sub [::checklist-subs/checklist-templates])))
              (seq checklists))
      [section/section
       "checklists-section"
       (util/mmap tr)
       (tr [:inspection/checklist])
       [:div.checklist
        (futil/for-all [checklist checklists]
          ^{:key (:db/id checklist)}
          [checklist-section (merge props (util/mmap lang checklist deficiencies?))])
        [add-checklist (merge props
                              (util/mmap checklists))]]])))
